import { ComponentInternalInstance, getCurrentInstance, Ref, ref } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import { CONSTANT_URL } from '@/renderer/scripts/http';
import { IS_BROWSER, IS_DEV, IS_TEST } from '@/common/constant/platform';

interface RecurringOptions {
  label: string;
  value: number;
}
interface RecurringRule {
  recurring_type: number;
  until_type: number;
  until_date: number;
  until_count: number;
}
interface MeetingConfig {
  subject: string;
  type: number;
  start_time: string;
  end_time: string;
  meeting_type: string;
  password: string;
  recurring_rule: RecurringRule;
}
interface ScheduleMeeting {
  meetingConfig: MeetingConfig;
  enable_doc_upload_permission: boolean;
  auto_record_type?: 'cloud' | 'none' | 'local';
}
const recurringOptions: RecurringOptions[] = [
  {
    label: i18n_t('每天'),
    value: 0,
  },
  {
    label: i18n_t('每个工作日'),
    value: 1,
  },
  {
    label: i18n_t('每周'),
    value: 2,
  },
  {
    label: i18n_t('每两周'),
    value: 3,
  },
  {
    label: i18n_t('每月'),
    value: 4,
  },
];
type Handler = {
  showJoinMeeting: Ref<boolean>;
  showScheduleMeeting: Ref<boolean>;
  meetingCode: Ref<string | number>;
  recurringOptions: RecurringOptions[];
  meetingLists: Ref<SMH.meetingListInfo[]>;
  methods: {
    authMeeting: (authCode: string, meetingAction: string) => void;
    joinMeeting: () => Promise<string>;
    scheduleMeeting: (formData: any) => Promise<string>;
    getMeetingLists: () => void;
    handleJoinMeeting: () => void;
    handleQuickMeeting: () => void;
    handleScheduleMeeting: () => void;
  };
};
export default function handler(): Handler {
  const vm = (getCurrentInstance() as ComponentInternalInstance).proxy;
  const store = vm.$store;
  const showJoinMeeting = ref(false);
  const showScheduleMeeting = ref(false);
  const meetingCode = ref('');
  const meetingLists: Ref<SMH.meetingListInfo[]> = ref([]);
  const methods = {
    // 检查是否有授权
    async checkMeetingAuth(action: string) {
      const { data } = await smh.userAPI.checkMeetingAuth();
      if (!data.available) {
        if (IS_BROWSER) {
          const nowUrl = window.location.href;
          const redirectUrl = `${nowUrl}&meetingAction=${action}`;
          dispatchRouter(CONSTANT_URL.meetingAuthUrl + encodeURIComponent(redirectUrl), 'location');
        } else {
          let prefix = '';
          if (IS_DEV || IS_TEST) {
            prefix = 'test.';
          }
          // 客户端重定向只需要传递action动作，且路由必须为meetingAuth，这样可以在meeting的组件中进行打开客户端的scheme重定向
          const redirectUrl = `${store.getters.protocol}://${prefix}pan.tencent.com/web/meetingAuth?meetingAction=${action}`;
          dispatchRouter(CONSTANT_URL.meetingAuthUrl + encodeURIComponent(redirectUrl));
        }
      }
      return data.available;
    },
    // 根据授权的重定向链接里的authCode进行授权
    async authMeeting(authCode: string, meetingAction: string) {
      try {
        const res = await smh.userAPI.authMeeting({
          authCode,
        });
        if (res?.status === 204) {
          methods.handleMeetingAction(meetingAction);
        } else {
          logger.ui('tencentMeeting auth info', res.data.message);
        }
      } catch (err: MsgError) {
        tips.error(err);
      }
    },
    async joinMeeting() {
      try {
        const res = await smh.userAPI.joinMeetingByCode({
          meetingCode: meetingCode.value,
        });
        const { data } = res;
        if (data.error_code && data.message) {
          tips.error(data.message);
          return '';
        }
        return data.joinUrl as string;
      } catch (err) {
        tips.error(err as MsgError);
        return '';
      }
    },
    async quickMeeting() {
      try {
        const opt = {
          meetingConfig: {
            subject: i18n_t('腾讯会议'),
            type: 1,
            start_time: Math.round(Date.now() / 1000).toString(),
            end_time: Math.round(Date.now() / 1000 + 1000).toString(),
          },
        };
        const { data } = await smh.userAPI.createMeeting(opt);
        if (data.error_code && data.message) {
          tips.error(data.message);
        } else {
          dispatchRouter(data.joinUrl as string);
        }
      } catch (err: MsgError) {
        tips.error(err);
      }
    },
    async getMeetingLists() {
      try {
        const { data } = await smh.userAPI.getMeetingLists();
        meetingLists.value = data.meeting_info_list;
      } catch (err: MsgError) {
        tips.error(err);
      }
    },
    async scheduleMeeting(opt: ScheduleMeeting) {
      try {
        const { data } = await smh.userAPI.createMeeting(opt);
        if (data.error_code && data.message) {
          tips.error(data.message);
          return '';
        }
        return data.joinUrl as string;
      } catch (err: MsgError) {
        tips.error(err);
        return '';
      }
    },
    async handleJoinMeeting() {
      try {
        const available = await methods.checkMeetingAuth('joinMeeting');
        if (available) {
          showJoinMeeting.value = true;
        }
      } catch (err: MsgError) {
        tips.error(err);
      }
    },
    async handleQuickMeeting() {
      try {
        const available = await methods.checkMeetingAuth('quickMeeting');
        if (available) {
          methods.quickMeeting();
        }
      } catch (err: MsgError) {
        tips.error(err);
      }
    },
    async handleScheduleMeeting() {
      try {
        const available = await methods.checkMeetingAuth('scheduleMeeting');
        if (available) {
          showScheduleMeeting.value = true;
        }
      } catch (err: MsgError) {
        tips.error(err);
      }
    },
    handleBindMeeting() {
      tips.success(i18n_t('绑定成功'));
      // web端会从meetingAuth跳转进工作台 pc端只需要刷新企业信息
      if (IS_BROWSER) {
        dispatchRouter({ name: 'workbench' });
      } else {
        store.dispatch('getUserBaseInfo');
      }
    },
    handleMeetingAction(meetingAction: string) {
      switch (meetingAction) {
        case 'joinMeeting':
          methods.handleJoinMeeting();
          break;
        case 'quickMeeting':
          methods.handleQuickMeeting();
          break;
        case 'scheduleMeeting':
          methods.handleScheduleMeeting();
          break;
        case 'bindMeeting':
          methods.handleBindMeeting();
          break;
        default:
          break;
      }
    },
  };
  return {
    recurringOptions,
    showScheduleMeeting,
    showJoinMeeting,
    methods,
    meetingCode,
    meetingLists,
  };
}
